import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "./styles/ServiceNoteModal.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  driverName?: string | null;
  loading?: boolean;
  onConfirm: (comment: string) => void | Promise<void>;
};

const ReleaseDriverModal: React.FC<Props> = ({
  isOpen,
  onClose,
  driverName,
  loading = false,
  onConfirm,
}) => {
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setComment("");
      setError("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !loading) onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = comment.trim();
    if (!cleaned) {
      setError("El comentario es obligatorio.");
      return;
    }
    setError("");
    await onConfirm(cleaned);
  };

  const content = (
    <div
      className="service-note-overlay"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div className="service-note-content" onClick={(e) => e.stopPropagation()}>
        <div className="service-note-header">
          <h3 className="service-note-title">Desasignar conductor</h3>
          <button
            type="button"
            className="service-note-close"
            onClick={onClose}
            aria-label="Cerrar"
            disabled={loading}
          >
            <X size={22} />
          </button>
        </div>

        <p style={{ margin: "0 0 1rem", color: "#475569", fontSize: "0.95rem", lineHeight: 1.45 }}>
          {driverName
            ? `Se desasignará a ${driverName} del servicio. Indica el motivo del cambio.`
            : "Se desasignará el conductor del servicio. Indica el motivo del cambio."}
        </p>

        <form className="service-note-form" onSubmit={handleSubmit}>
          <div className="service-note-form-field">
            <label htmlFor="release-driver-comment">Comentario</label>
            <textarea
              id="release-driver-comment"
              className="service-note-form-textarea"
              rows={4}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                if (error) setError("");
              }}
              placeholder="Motivo por el que se desasigna el conductor…"
              disabled={loading}
              autoFocus
            />
            {error ? <span className="service-note-form-error">{error}</span> : null}
          </div>

          <div className="service-note-form-actions">
            <button
              type="button"
              className="service-note-form-btn service-note-form-btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="service-note-form-btn service-note-form-btn-submit"
              disabled={loading}
            >
              {loading ? "Desasignando…" : "Desasignar conductor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ReleaseDriverModal;
